/**
 * AudioNotificationManager - PREMIUM EDITION
 * High-quality professional notification sounds
 * Similar to Slack, Discord, Apple, Google services
 * 
 * Features:
 * - Advanced audio synthesis
 * - Multiple premium sound options
 * - High-quality audio processing
 * - Professional production values
 * - Volume control
 * - Browser compatibility
 */

class AudioNotificationManager {
  constructor() {
    this.audioContext = null;
    this.isInitialized = false;
    this.lastPlayTime = 0;
    this.minInterval = 800; // Minimum 0.8 seconds between sounds
    this.settings = this.loadSettings();
  }

  /**
   * Initialize the audio manager
   */
  initialize() {
    if (this.isInitialized) return;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        this.audioContext = new AudioContext();
        this.isInitialized = true;
        console.log('✅ Premium Audio System initialized');
      }
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      this.isInitialized = false;
    }
  }

  /**
   * PREMIUM SOUND 1: "DISCORD" - Like Discord notification
   * Modern, sophisticated, smooth
   */
  playDiscordSound(volume = 0.7) {
    if (!this.audioContext) return;

    try {
      const now = this.audioContext.currentTime;
      const ctx = this.audioContext;
      
      // Create master gain
      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      masterGain.gain.setValueAtTime(volume * 0.8, now);

      // Create compressor for polish
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-30, now);
      compressor.knee.setValueAtTime(40, now);
      compressor.ratio.setValueAtTime(12, now);
      compressor.attack.setValueAtTime(0.003, now);
      compressor.release.setValueAtTime(0.25, now);
      compressor.connect(masterGain);

      // Two sophisticated notes
      const notes = [
        { freq: 659.25, time: 0, duration: 0.15 },     // E5
        { freq: 783.99, time: 0.1, duration: 0.25 }    // G5
      ];

      notes.forEach(note => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        // Sine wave for smoothness
        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, now + note.time);

        // Low-pass filter for warmth
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(8000, now + note.time);
        filter.Q.setValueAtTime(0.5, now + note.time);

        // Connect chain
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(compressor);

        // Envelope with smooth attack
        gain.gain.setValueAtTime(0, now + note.time);
        gain.gain.linearRampToValueAtTime(volume * 0.7, now + note.time + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, now + note.time + note.duration);

        osc.start(now + note.time);
        osc.stop(now + note.time + note.duration);
      });

      console.log('🎵 Discord-style notification played');
    } catch (error) {
      console.error('Error playing Discord sound:', error);
    }
  }

  /**
   * PREMIUM SOUND 2: "SLACK" - Like Slack notification
   * Modern, subtle, distinctive
   */
  playSlackSound(volume = 0.7) {
    if (!this.audioContext) return;

    try {
      const now = this.audioContext.currentTime;
      const ctx = this.audioContext;
      
      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      masterGain.gain.setValueAtTime(volume * 0.75, now);

      // Create reverb effect using delay nodes
      const dryGain = ctx.createGain();
      const wetGain = ctx.createGain();
      dryGain.gain.setValueAtTime(0.7, now);
      wetGain.gain.setValueAtTime(0.3, now);
      
      const delay = ctx.createDelay(0.5);
      delay.delayTime.setValueAtTime(0.08, now);
      
      dryGain.connect(masterGain);
      wetGain.connect(delay);
      delay.connect(masterGain);

      // Single sophisticated note
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, now);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(7000, now);

      osc.connect(filter);
      filter.connect(dryGain);
      filter.connect(wetGain);

      // Smooth envelope
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume * 0.6, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

      osc.start(now);
      osc.stop(now + 0.35);

      console.log('🎵 Slack-style notification played');
    } catch (error) {
      console.error('Error playing Slack sound:', error);
    }
  }

  /**
   * PREMIUM SOUND 3: "BELL" - Beautiful premium bell
   * Elegant, sophisticated, timeless
   */
  playBellSound(volume = 0.7) {
    if (!this.audioContext) return;

    try {
      const now = this.audioContext.currentTime;
      const ctx = this.audioContext;

      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      masterGain.gain.setValueAtTime(volume * 0.8, now);

      // Bell is a complex sound - multiple harmonics
      const bellFreqs = [
        { freq: 523.25, amp: 1.0, decay: 0.8 },    // C5 - fundamental
        { freq: 783.99, amp: 0.6, decay: 0.7 },    // G5 - major third
        { freq: 1046.50, amp: 0.4, decay: 0.6 },   // C6 - octave
        { freq: 1174.66, amp: 0.3, decay: 0.5 }    // D6 - harmonics
      ];

      bellFreqs.forEach(note => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, now);

        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(9000, now);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(masterGain);

        // Bell-like decay
        gain.gain.setValueAtTime(volume * 0.6 * note.amp, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + note.decay);

        osc.start(now);
        osc.stop(now + note.decay);
      });

      console.log('🎵 Premium bell notification played');
    } catch (error) {
      console.error('Error playing bell sound:', error);
    }
  }

  /**
   * PREMIUM SOUND 4: "CHIME" - Elegant musical chime
   * Sophisticated, musical, pleasant
   */
  playChimeSound(volume = 0.7) {
    if (!this.audioContext) return;

    try {
      const now = this.audioContext.currentTime;
      const ctx = this.audioContext;

      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      masterGain.gain.setValueAtTime(volume * 0.75, now);

      // Chord progression: C-E-G (C major chord)
      const chordNotes = [
        { freq: 523.25, time: 0, duration: 0.4 },      // C5
        { freq: 659.25, time: 0.05, duration: 0.45 },  // E5
        { freq: 783.99, time: 0.1, duration: 0.5 }     // G5
      ];

      chordNotes.forEach(note => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, now + note.time);

        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(8500, now + note.time);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(masterGain);

        // Musical envelope
        gain.gain.setValueAtTime(0, now + note.time);
        gain.gain.linearRampToValueAtTime(volume * 0.65, now + note.time + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, now + note.time + note.duration);

        osc.start(now + note.time);
        osc.stop(now + note.time + note.duration);
      });

      console.log('🎵 Premium chime notification played');
    } catch (error) {
      console.error('Error playing chime sound:', error);
    }
  }

  /**
   * PREMIUM SOUND 5: "GLASS" - Crystalline glass sound
   * Clean, modern, distinctive
   */
  playGlassSound(volume = 0.7) {
    if (!this.audioContext) return;

    try {
      const now = this.audioContext.currentTime;
      const ctx = this.audioContext;

      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      masterGain.gain.setValueAtTime(volume * 0.8, now);

      // Add high-frequency harmonics for "glass" effect
      const glassFreqs = [
        { freq: 1174.66, amp: 1.0 },     // D6 - main tone
        { freq: 1864.66, amp: 0.5 },     // B6 - overtone
        { freq: 2349.32, amp: 0.3 }      // D7 - bright sparkle
      ];

      glassFreqs.forEach(note => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, now);

        osc.connect(gain);
        gain.connect(masterGain);

        // Quick bright decay
        gain.gain.setValueAtTime(volume * 0.7 * note.amp, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

        osc.start(now);
        osc.stop(now + 0.25);
      });

      console.log('🎵 Premium glass notification played');
    } catch (error) {
      console.error('Error playing glass sound:', error);
    }
  }

  /**
   * PREMIUM SOUND 6: "ELEGANT" - Premium elegant notification
   * High-end, sophisticated, smooth
   */
  playElegantSound(volume = 0.7) {
    if (!this.audioContext) return;

    try {
      const now = this.audioContext.currentTime;
      const ctx = this.audioContext;

      const masterGain = ctx.createGain();
      masterGain.connect(ctx.destination);
      masterGain.gain.setValueAtTime(volume * 0.75, now);

      // Smooth gliding between notes
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      const gain2 = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      // First oscillator
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(659.25, now);
      osc1.frequency.exponentialRampToValueAtTime(587.33, now + 0.3);

      gain1.gain.setValueAtTime(0, now);
      gain1.gain.linearRampToValueAtTime(volume * 0.6, now + 0.02);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

      // Second oscillator - harmony
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(523.25, now + 0.1);
      osc2.frequency.exponentialRampToValueAtTime(493.88, now + 0.35);

      gain2.gain.setValueAtTime(0, now + 0.1);
      gain2.gain.linearRampToValueAtTime(volume * 0.4, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

      // Filter
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(8000, now);
      filter.frequency.exponentialRampToValueAtTime(5000, now + 0.4);

      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(masterGain);

      osc1.start(now);
      osc1.stop(now + 0.4);

      osc2.start(now + 0.1);
      osc2.stop(now + 0.45);

      console.log('🎵 Premium elegant notification played');
    } catch (error) {
      console.error('Error playing elegant sound:', error);
    }
  }

  /**
   * Play notification sound
   */
  playSound(soundName = 'discord', options = {}) {
    if (!this.isInitialized) {
      console.warn('AudioNotificationManager not initialized');
      return;
    }

    if (!this.settings.enabled) {
      console.log('🔇 Sound notifications disabled');
      return;
    }

    const now = Date.now();
    if (now - this.lastPlayTime < this.minInterval) {
      console.log('⏱️ Sound spam prevention');
      return;
    }

    const volume = options.volume !== undefined ? options.volume : this.settings.volume;

    try {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().then(() => {
          this.playSoundByType(soundName, volume);
        });
      } else {
        this.playSoundByType(soundName, volume);
      }

      this.lastPlayTime = now;
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }

  /**
   * Play sound by type
   */
  playSoundByType(soundName, volume) {
    switch (soundName) {
      case 'discord':
        this.playDiscordSound(volume);
        break;
      case 'slack':
        this.playSlackSound(volume);
        break;
      case 'bell':
        this.playBellSound(volume);
        break;
      case 'chime':
        this.playChimeSound(volume);
        break;
      case 'glass':
        this.playGlassSound(volume);
        break;
      case 'elegant':
        this.playElegantSound(volume);
        break;
      default:
        this.playDiscordSound(volume);
    }
  }

  /**
   * Stop sound
   */
  stopSound() {
    console.log('⏹️ Sound stopped');
  }

  /**
   * Set volume
   */
  setVolume(volume) {
    const validVolume = Math.min(1, Math.max(0, volume));
    this.settings.volume = validVolume;
    this.saveSettings();
    console.log(`🔊 Volume set to: ${Math.round(validVolume * 100)}%`);
  }

  /**
   * Enable/disable sound
   */
  setEnabled(enabled) {
    this.settings.enabled = enabled;
    this.saveSettings();
    console.log(`${enabled ? '🔔' : '🔇'} Sound ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Get settings
   */
  getSettings() {
    return { ...this.settings };
  }

  /**
   * Load settings
   */
  loadSettings() {
    try {
      const saved = localStorage.getItem('audioNotificationSettings');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load settings:', error);
    }

    return {
      enabled: true,
      volume: 0.6,
      soundType: 'discord',
      minInterval: 800,
    };
  }

  /**
   * Save settings
   */
  saveSettings() {
    try {
      localStorage.setItem('audioNotificationSettings', JSON.stringify(this.settings));
    } catch (error) {
      console.warn('Failed to save settings:', error);
    }
  }

  /**
   * Get available sounds
   */
  getAvailableSounds() {
    return ['discord', 'slack', 'bell', 'chime', 'glass', 'elegant'];
  }

  /**
   * Test sound
   */
  testSound(soundName = 'discord') {
    console.log(`🎵 Testing: ${soundName}`);
    this.playSound(soundName, { volume: this.settings.volume });
  }
}

const audioNotificationManager = new AudioNotificationManager();
export default audioNotificationManager;